import { useEffect, useState } from "react";

const DAY_MS = 86400000;

interface FocusTask {
  start_date?: string | null;
  end_date?: string | null;
  status?: string;
}

interface FocusJob {
  job_tasks?: FocusTask[];
}

/** Start/end of every task the Job Schedule draws (scheduled and not completed). */
function drawnTaskSpans(jobs: FocusJob[]): { startMs: number; endMs: number }[] {
  const spans: { startMs: number; endMs: number }[] = [];
  jobs.forEach((job) =>
    (job.job_tasks || []).forEach((jt) => {
      if (!jt.start_date || jt.status === "completed") return;
      const startMs = new Date(jt.start_date).getTime();
      const endMs = jt.end_date ? new Date(jt.end_date).getTime() : startMs;
      if (Number.isFinite(startMs)) spans.push({ startMs, endMs: Math.max(endMs, startMs) });
    })
  );
  return spans;
}

/**
 * Where to center the chart for a set of jobs: now if any of their tasks is
 * running now, otherwise the start of the earliest task that has not finished
 * yet, otherwise the earliest task. Now when there are no tasks.
 */
export function focusDate(jobs: FocusJob[], nowMs: number): number {
  const spans = drawnTaskSpans(jobs);
  if (spans.length === 0) return nowMs;
  if (spans.some((s) => s.startMs <= nowMs && s.endMs >= nowMs)) return nowMs;
  const upcoming = spans.filter((s) => s.endMs >= nowMs);
  const pool = upcoming.length ? upcoming : spans;
  return Math.min(...pool.map((s) => s.startMs));
}

/** Days kept on each side of today, wide enough to center today at that scale. */
export const GANTT_NOW_PAD_DAYS: Record<string, number> = {
  day: 14,
  week: 60,
  month: 240,
};

/**
 * Date range for the Gantt chart: the tasks' range, widened so today can be
 * centered even when every task is in the past or the future.
 */
export function ganttRange(
  tasks: { start: Date; end: Date }[],
  nowMs: number,
  scale: string
): { start: Date; end: Date } {
  const padMs = (GANTT_NOW_PAD_DAYS[scale] ?? GANTT_NOW_PAD_DAYS.day) * DAY_MS;
  let min = nowMs - padMs;
  let max = nowMs + padMs;
  tasks.forEach((t) => {
    min = Math.min(min, t.start.getTime());
    max = Math.max(max, t.end.getTime());
  });
  return { start: new Date(min), end: new Date(max) };
}

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Current time, refreshed every `intervalMs`, so "now" markers do not go stale. */
export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
